import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react';

import useUser from 'hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import useUI from 'hooks/use-ui';
import EmptyLoadingState from 'components/empty-loading-state';
import useEnterpriseDutchieLoyalty from 'hooks/use-enterprise-dutchie-loyalty';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useGetDutchiePosLoyaltyConfig } from 'src/components/loyalty/use-get-dutchie-pos-loyalty-config';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { useSearchEnterprise } from '../hooks/use-search-enterprise';
import { LandingPage } from './components/landing-page';
import { LoginMethodSelection } from './components/login-method-selection';
import { EnterpriseSignupContent } from './components/enterprise-signup-content';
import { LoyaltyRewards } from './components/loyalty-rewards';
import type { FlowStep } from './types';
import { FLOW_STEPS } from './types';

export function EnterpriseSignup(): JSX.Element {
  const router = useRouter();
  const { enterpriseId, cName, state: stateFromUrl } = router?.query ?? {};
  const User = useUser();
  const showErnie = useErnie();

  // Flow state - 4 step flow: landing -> login-method -> email-signup -> loyalty-rewards
  const [currentStep, setCurrentStep] = useState<FlowStep>(FLOW_STEPS.LANDING);
  const [selectedState, setSelectedState] = useState('');
  const [canonicalEnterpriseId, setCanonicalEnterpriseId] = useState('');

  const [loyaltyEnrollmentComplete, setLoyaltyEnrollmentComplete] = useState(false);
  const [enrollmentAttempted, setEnrollmentAttempted] = useState(false);

  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const userLoading = useObserver(() => User.loading);
  const userId = useObserver(() => User.id);

  // Convert enterpriseId (or cName slug) to string for template usage
  const enterpriseIdRaw = enterpriseId ?? cName ?? '';
  const enterpriseIdString = Array.isArray(enterpriseIdRaw) ? enterpriseIdRaw[0] : enterpriseIdRaw;

  const { enterprise, loading: enterpriseLoading, error: enterpriseError } = useSearchEnterprise({
    enterpriseId: enterpriseIdString,
  });

  useEffect(() => {
    if (enterprise?.uniqueName && enterpriseIdString !== enterprise.uniqueName) {
      const slug = enterprise.uniqueName;
      const isStoresEnterpriseRoute = router?.pathname.startsWith('/stores/enterprise/');
      const isEnterpriseSignupRoute = router?.pathname.startsWith('/enterprise/');

      if (isStoresEnterpriseRoute) {
        void router?.replace(`/stores/enterprise/${slug}/signup`, undefined, { shallow: true });
      } else if (isEnterpriseSignupRoute) {
        void router?.replace(`/enterprise/${slug}/signup`, undefined, { shallow: true });
      }
    }
    if (enterprise?.enterpriseId && enterpriseIdString !== enterprise.enterpriseId) {
      setCanonicalEnterpriseId(enterprise.enterpriseId);
    }
  }, [enterprise?.uniqueName, enterprise?.enterpriseId, enterpriseId, enterpriseIdString, router]);

  useEffect(() => {
    if (router?.isReady && stateFromUrl) {
      const stateParam = Array.isArray(stateFromUrl) ? stateFromUrl[0] : stateFromUrl;
      if (stateParam && stateParam !== selectedState) {
        setSelectedState(stateParam);
      }
    }
  }, [router?.isReady, stateFromUrl, selectedState]);

  const {
    createEnterpriseDutchiePosLoyaltyMember,
    loading: loyaltyLoading,
    error: loyaltyError,
  } = useEnterpriseDutchieLoyalty();

  // Use canonical enterprise ID if available, otherwise use the one from URL
  const activeEnterpriseId = canonicalEnterpriseId || enterpriseIdString;

  // Handle loyalty enrollment errors
  useEffect(() => {
    if (loyaltyError) {
      console.error('Loyalty enrollment error:', loyaltyError);
      showErnie('Error enrolling in rewards program. You can try again later.', 'danger');
    }
  }, [loyaltyError, showErnie]);

  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const skip = !selectedState || (!userId && !birthday && !isLoggedIn);
  const skipRewards = skip || !phone;

  const { data: rewards, loading: rewardsDataLoadingResult, refetch: refetchRewards } = useGetRewards({
    enterpriseId: activeEnterpriseId,
    isoRegionCode: selectedState ? `US-${selectedState}` : undefined,
    phone: phone ?? '',
    birthday: birthday ?? '',
    pin: 'skip',
    customerId: userId,
    skip: skipRewards,
  });

  const {
    data: loyaltyConfigData,
    loading: loyaltyConfigLoadingResult,
    refetch: refetchLoyaltyConfig,
  } = useGetDutchiePosLoyaltyConfig({
    enterpriseId: activeEnterpriseId,
    isoRegionCode: selectedState ? `US-${selectedState}` : undefined,
    skip,
  });

  const UI = useUI();
  const dispensaryId = loyaltyConfigData?.dispensaryId;
  const { dispensary: themeDispensary } = useDispensary(dispensaryId ?? undefined);

  useEffect(() => {
    if (themeDispensary) {
      UI.setEnterpriseDispensary(themeDispensary);
    }
    return () => {
      UI.setEnterpriseDispensary(null);
    };
  }, [UI, themeDispensary]);

  // Refetch hooks when selectedState changes
  useEffect(() => {
    if (userId && selectedState && isLoggedIn && phone && birthday) {
      void refetchRewards();
      void refetchLoyaltyConfig();
    }
  }, [selectedState, isLoggedIn, refetchRewards, refetchLoyaltyConfig, userId, phone, birthday]);

  // Enroll user in loyalty when they become logged in and have selected a state
  useEffect(() => {
    const enrollUserInLoyalty = async (): Promise<void> => {
      if (
        !isLoggedIn ||
        !userId ||
        !selectedState ||
        userLoading ||
        loyaltyLoading ||
        loyaltyEnrollmentComplete ||
        enrollmentAttempted ||
        enterpriseLoading ||
        !enterprise
      ) {
        return;
      }
      setEnrollmentAttempted(true);

      // If user is already a loyalty member, set the enrollment complete flag and show the success message
      if (rewards?.getRewardsV2?.isLoyaltyMember && loyaltyConfigData) {
        setLoyaltyEnrollmentComplete(true);
        showErnie(`Welcome to ${String(enterprise.name || 'the')} rewards program!`, 'success');
        // Move to loyalty-rewards and enroll
        if (currentStep !== FLOW_STEPS.LOYALTY_REWARDS) {
          setCurrentStep(FLOW_STEPS.LOYALTY_REWARDS);
        }
      }

      if (!rewards?.getRewardsV2?.isLoyaltyMember && loyaltyConfigData) {
        try {
          // request rewards and loylatyConfig after each state selection and then run this call or not

          await createEnterpriseDutchiePosLoyaltyMember({
            variables: {
              enterpriseId: activeEnterpriseId,
              userId,
              isoRegionCode: `US-${selectedState}`,
            },
          });
          void refetchRewards();

          setLoyaltyEnrollmentComplete(true);
          showErnie(`Welcome to ${String(enterprise.name || 'the')} rewards program!`, 'success');

          // Move to loyalty-rewards and enroll
          if (currentStep !== FLOW_STEPS.LOYALTY_REWARDS) {
            setCurrentStep(FLOW_STEPS.LOYALTY_REWARDS);
          }
        } catch (error) {
          console.error('Error enrolling in enterprise loyalty:', error);
          showErnie('Error enrolling in rewards program. You can try again later.', 'danger');
        }
      }
    };

    if (userLoading) {
      return;
    }
    if (
      isLoggedIn &&
      selectedState &&
      !loyaltyEnrollmentComplete &&
      !enrollmentAttempted &&
      !rewardsDataLoadingResult &&
      !loyaltyConfigLoadingResult
    ) {
      void enrollUserInLoyalty();
    }
  }, [
    loyaltyConfigData,
    rewards?.getRewardsV2,
    rewardsDataLoadingResult,
    loyaltyConfigLoadingResult,
    isLoggedIn,
    userLoading,
    userId,
    selectedState,
    currentStep,
    loyaltyLoading,
    loyaltyEnrollmentComplete,
    enrollmentAttempted,
    enterpriseLoading,
    enterprise,
    activeEnterpriseId,
    createEnterpriseDutchiePosLoyaltyMember,
    showErnie,
    refetchRewards,
  ]);

  const handleStartEarningRewards = (stateCode: string): void => {
    setSelectedState(stateCode);

    if (router) {
      void router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, state: stateCode },
        },
        undefined,
        { shallow: true }
      );
    }

    // If not logged in, go to login flow
    // If already logged in, the enrollment effect will handle moving to loyalty-rewards
    if (!isLoggedIn) {
      setCurrentStep(FLOW_STEPS.LOGIN_METHOD);
    }
  };

  const handleContinueWithEmail = (): void => {
    setCurrentStep(FLOW_STEPS.EMAIL_SIGNUP);
  };

  if (enterpriseLoading || !enterprise || rewardsDataLoadingResult || loyaltyConfigLoadingResult) {
    return <EmptyLoadingState isLoading page='enterprise-signup' />;
  }

  if (enterpriseError) {
    console.error('Enterprise error:', {
      enterpriseError,
      enterpriseId: enterpriseIdString,
    });
    return <EmptyLoadingState isLoading={false} page='enterprise-signup' />;
  }

  if (loyaltyLoading) {
    return <EmptyLoadingState isLoading page='enterprise-signup' />;
  }

  if (currentStep === FLOW_STEPS.LANDING) {
    return <LandingPage enterprise={enterprise} onStartEarningRewards={handleStartEarningRewards} />;
  }

  if (currentStep === FLOW_STEPS.LOGIN_METHOD) {
    return <LoginMethodSelection onContinueWithEmail={handleContinueWithEmail} />;
  }

  if (currentStep === FLOW_STEPS.EMAIL_SIGNUP) {
    return <EnterpriseSignupContent />;
  }

  return (
    <LoyaltyRewards
      enterprise={enterprise}
      rewards={rewards?.getRewardsV2}
      loyaltyConfigData={loyaltyConfigData}
      rewardsDataLoading={rewardsDataLoadingResult}
      loyaltyConfigLoading={loyaltyConfigLoadingResult}
    />
  );
}
